// BatchResults.tsx
// Ranked table view showing all evaluated papers side by side.
// Shows time savings summary and supports multiple export formats.
// Clicking a row navigates to that paper's full detail view.

import React, { useState, useRef, useEffect } from 'react';
import type { EvaluationResult, BatchResult, Verdict, PaperType, WhatToRead, Theme } from '../types';
import {
  VERDICT_LABELS,
  PAPER_TYPE_SHORT,
  WHAT_TO_READ_SHORT,
} from '../utils/formatters';
import {
  exportMarkdown,
  exportObsidianVault,
  exportCSV,
  exportNotionCSV,
  calculateBatchTime,
  estimateReadingTime,
} from '../utils/export';
import ThemeToggle from './ThemeToggle';
import batchResultsStyles from '../styles/BatchResults.module.css';

interface BatchResultsProps {
  batch: BatchResult;
  onReset: () => void;
  onSelectPaper: (result: EvaluationResult) => void;
  onBack?: () => void;
  theme: Theme;
  onToggleTheme: () => void;
}

const VERDICT_COLORS: Record<Verdict, { bg: string; color: string; border: string }> = {
  must_read: { bg: 'var(--verdict-must-bg)', color: 'var(--verdict-must)', border: 'var(--verdict-must-border)' },
  worth_it: { bg: 'var(--verdict-worth-bg)', color: 'var(--verdict-worth)', border: 'var(--verdict-worth-border)' },
  skim: { bg: 'var(--verdict-skim-bg)', color: 'var(--verdict-skim)', border: 'var(--verdict-skim-border)' },
  skip: { bg: 'var(--verdict-skip-bg)', color: 'var(--verdict-skip)', border: 'var(--verdict-skip-border)' },
};

export default function BatchResults({
  batch,
  onReset,
  onSelectPaper,
  onBack,
  theme,
  onToggleTheme,
}: BatchResultsProps) {
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const exportRef = useRef<HTMLDivElement>(null);

  const sorted = [...batch.results].sort((a, b) => {
    if (a.error && !b.error) return 1;
    if (!a.error && b.error) return -1;
    return (b.overall ?? 0) - (a.overall ?? 0);
  });

  const validResults = sorted.filter(r => !r.error) as EvaluationResult[];
  const timeStats = calculateBatchTime(batch.results);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = async (format: string) => {
    setExporting(format);
    setExportOpen(false);
    try {
      switch (format) {
        case 'markdown':
          exportMarkdown(batch.results);
          break;
        case 'obsidian':
          await exportObsidianVault(batch.results);
          break;
        case 'csv':
          exportCSV(batch.results);
          break;
        case 'notion':
          exportNotionCSV(batch.results);
          break;
      }
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className={batchResultsStyles.page}>
      <div className={batchResultsStyles.container}>

        {/* ── Header ── */}
        <div className={batchResultsStyles.header}>
          <h1 className={batchResultsStyles.headerTitle}>Reading Plan</h1>
          <div className={batchResultsStyles.headerActions}>
            {onBack && (
              <button
                className={batchResultsStyles.btnReset}
                onClick={onBack}
              >
                ← Dashboard
              </button>
            )}

            {/* Export dropdown */}
            <div ref={exportRef} style={{ position: 'relative' }}>
              <button
                className={batchResultsStyles.btnExport}
                onClick={() => setExportOpen(o => !o)}
                disabled={!!exporting}
              >
                {exporting ? `Exporting…` : '↓ Export'}
                <span style={{ marginLeft: 4, fontSize: 10 }}>▾</span>
              </button>

              {exportOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 4,
                  background: 'var(--bg-card)',
                  border: '1px solid var(--bg-card-border)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-lift)',
                  minWidth: 200,
                  zIndex: 100,
                  overflow: 'hidden',
                }}>
                  {[
                    { key: 'markdown', icon: '📄', label: 'Markdown', desc: 'Reading plan with checklists' },
                    { key: 'obsidian', icon: '🔮', label: 'Obsidian Vault', desc: 'ZIP of linked .md files' },
                    { key: 'notion', icon: '◻️', label: 'Notion CSV', desc: 'Import as Notion database' },
                    { key: 'csv', icon: '📊', label: 'CSV', desc: 'Excel / Sheets / systematic review' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => handleExport(opt.key)}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        width: '100%',
                        padding: '10px 14px',
                        background: 'none',
                        border: 'none',
                        borderBottom: '1px solid var(--bg-card-border)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--accent-bg)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                    >
                      <span style={{ fontSize: 16, flexShrink: 0 }}>{opt.icon}</span>
                      <div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
                          {opt.label}
                        </div>
                        <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                          {opt.desc}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              className={batchResultsStyles.btnPrint}
              onClick={() => window.print()}
            >
              🖨 Print
            </button>
            <button
              className={batchResultsStyles.btnReset}
              onClick={onReset}
            >
              + Evaluate another
            </button>
          </div>
        </div>

        {/* ── Time savings banner ── */}
        {validResults.length > 1 && (
          <div style={{
            display: 'flex',
            gap: 16,
            padding: '12px 18px',
            background: 'var(--accent-bg)',
            border: '1px solid var(--bg-card-border)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 16,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>
              Without triage:
              <span style={{ color: 'var(--text-primary)', fontWeight: 700, marginLeft: 6 }}>
                {timeStats.totalIfReadAllLabel}
              </span>
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>
              →
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>
              After triage:
              <span style={{ color: 'var(--text-primary)', fontWeight: 700, marginLeft: 6 }}>
                {timeStats.totalAfterTriageLabel}
              </span>
            </div>
            <div style={{
              marginLeft: 'auto',
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--accent)',
            }}>
              ⏱ You saved {timeStats.savedLabel}
            </div>
          </div>
        )}

        <p className={batchResultsStyles.summary}>
          {validResults.length} papers evaluated — ranked by overall score — click any row for full detail
        </p>

        {/* ── Table ── */}
        <table className={batchResultsStyles.table}>
          <thead className={batchResultsStyles.thead}>
            <tr>
              <th className={batchResultsStyles.th}>#</th>
              <th className={batchResultsStyles.th}>Paper</th>
              <th className={batchResultsStyles.th}>Type</th>
              <th className={batchResultsStyles.th}>Verdict</th>
              <th className={`${batchResultsStyles.th} ${batchResultsStyles.thRight}`}>Overall</th>
              <th className={`${batchResultsStyles.th} ${batchResultsStyles.thRight}`}>Read</th>
              <th className={`${batchResultsStyles.th} ${batchResultsStyles.thRight}`}>Novel</th>
              <th className={`${batchResultsStyles.th} ${batchResultsStyles.thRight}`}>Rigor</th>
              <th className={batchResultsStyles.th}>What to read</th>
              <th className={`${batchResultsStyles.th} ${batchResultsStyles.thRight}`}>Time</th>
              <th className={batchResultsStyles.th}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const vc = r.verdict ? VERDICT_COLORS[r.verdict as Verdict] : null;

              if (r.error) {
                return (
                  <tr key={i} className={`${batchResultsStyles.tr} ${batchResultsStyles.errorRow}`}>
                    <td className={`${batchResultsStyles.td} ${batchResultsStyles.rank}`}>{i + 1}</td>
                    <td className={`${batchResultsStyles.td} ${batchResultsStyles.filename}`}>{r.filename}</td>
                    <td className={batchResultsStyles.td} colSpan={8}>
                      <span className={batchResultsStyles.errorMsg}>Error: {r.error}</span>
                    </td>
                  </tr>
                );
              }

              const time = estimateReadingTime(r as EvaluationResult);

              return (
                <tr
                  key={i}
                  className={batchResultsStyles.tr}
                  onClick={() => onSelectPaper(r as EvaluationResult)}
                >
                  <td className={`${batchResultsStyles.td} ${batchResultsStyles.rank}`}>{i + 1}</td>
                  <td className={`${batchResultsStyles.td} ${batchResultsStyles.filename}`}>
                    {r.filename.replace('.pdf', '')}
                  </td>
                  <td className={batchResultsStyles.td}>
                    <span className={batchResultsStyles.typeBadge}>
                      {PAPER_TYPE_SHORT[r.detected_paper_type as PaperType]}
                    </span>
                  </td>
                  <td className={batchResultsStyles.td}>
                    {vc && (
                      <span
                        className={batchResultsStyles.verdictBadge}
                        style={{ background: vc.bg, color: vc.color, border: `1px solid ${vc.border}` }}
                      >
                        {VERDICT_LABELS[r.verdict as Verdict]}
                      </span>
                    )}
                  </td>
                  <td className={`${batchResultsStyles.td} ${batchResultsStyles.tdRight}`}>
                    <span
                      className={batchResultsStyles.score}
                      style={{
                        color: r.overall >= 4
                          ? 'var(--score-high)'
                          : r.overall >= 3
                          ? 'var(--score-mid)'
                          : 'var(--score-low)',
                      }}
                    >
                      {r.overall?.toFixed(1)}
                    </span>
                  </td>
                  <td className={`${batchResultsStyles.td} ${batchResultsStyles.tdRight} ${batchResultsStyles.miniScore}`}>
                    {r.readability?.score?.toFixed(1)}
                  </td>
                  <td className={`${batchResultsStyles.td} ${batchResultsStyles.tdRight} ${batchResultsStyles.miniScore}`}>
                    {r.novelty?.score?.toFixed(1)}
                  </td>
                  <td className={`${batchResultsStyles.td} ${batchResultsStyles.tdRight} ${batchResultsStyles.miniScore}`}>
                    {r.technical_rigor?.score?.toFixed(1)}
                  </td>
                  <td className={`${batchResultsStyles.td} ${batchResultsStyles.whatToRead}`}>
                    {WHAT_TO_READ_SHORT[r.what_to_read as WhatToRead]}
                  </td>
                  <td className={`${batchResultsStyles.td} ${batchResultsStyles.tdRight} ${batchResultsStyles.miniScore}`}>
                    {time.label}
                  </td>
                  <td className={batchResultsStyles.td}>
                    <span className={batchResultsStyles.chevron}>→</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ThemeToggle theme={theme} onToggle={onToggleTheme} />
    </div>
  );
}
